// resources/js/app.js
import './bootstrap'
import '../css/app.css'
import 'vue-select/dist/vue-select.css'



import { createApp, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { ZiggyVue } from '../../vendor/tightenco/ziggy'
import { normalizeRouteParams } from './routeParams'
import { createHead } from '@vueuse/head'
import canDirective from './directives/can'
import errorLogger from './services/errorLogger'
import { useTheme } from './composables/useTheme'

// Update CSRF token from Inertia page props
router.on('success', (event) => {
  const page = event.detail.page
  if (page.props.csrf_token) {
    const metaTag = document.head.querySelector('meta[name="csrf-token"]')
    if (metaTag) {
      metaTag.content = page.props.csrf_token
    }
  }
})

// Add CSRF token to all Inertia requests from meta tag
router.on('before', (event) => {
  const token = document.head.querySelector('meta[name="csrf-token"]')
  if (token) {
    event.detail.visit.headers['X-CSRF-TOKEN'] = token.content
  }

  // Reset body overflow before navigation to prevent modal artifacts
  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
  document.body.classList.remove('overflow-hidden')
})

// Handle all visit errors/failures
let isReloading = false

// Handle 419 Page Expired (CSRF token expired) - most reliable method
router.on('invalid', (event) => {
  if (isReloading) return

  const status = event.detail.response?.status
  if (status === 419) {
    event.preventDefault()
    isReloading = true
    // Sayfayı yenile - yeni CSRF token alınacak
    window.location.reload()
  }
})

router.on('error', (event) => {
  // Prevent multiple reloads
  if (isReloading) return

  const page = event.detail.page

  // Check various ways the 419 status might come through
  if (page?.props?.errors?.status === 419 ||
      page?.props?.errors?.response?.status === 419 ||
      event.detail.response?.status === 419) {
    isReloading = true
    setTimeout(() => window.location.reload(), 100)
  }
})

// Handle finish event to catch any response issues
router.on('finish', (event) => {
  // Prevent multiple reloads
  if (isReloading) return

  const page = event.detail.page

  // If no component property, response is invalid
  if (page && !page.component) {
    // Try to check if it's a 419 specifically
    fetch(window.location.href, {
      method: 'HEAD',
      credentials: 'same-origin'
    }).catch(() => {
      isReloading = true
      setTimeout(() => window.location.reload(), 100)
    })
  }
})

// Clean up modal/dialog artifacts on page navigation
router.on('navigate', () => {
  // Reset body overflow in case a modal left it hidden
  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
  document.body.classList.remove('overflow-hidden')

  // Remove any orphaned HeadlessUI portal containers
  const portalRoot = document.getElementById('headlessui-portal-root')
  if (portalRoot) {
    portalRoot.remove()
  }

  // Remove any stray HeadlessUI backdrop/portal elements
  document.querySelectorAll('[data-headlessui-portal]').forEach(el => {
    el.remove()
  })

  // Remove any orphaned teleported modal overlays (fixed inset-0 z-50 elements on body)
  document.querySelectorAll('body > div.fixed.inset-0[class*="z-50"]').forEach(el => {
    // Only remove if it's not part of the Vue app
    if (!el.closest('#app')) {
      el.remove()
    }
  })
})

const appName = import.meta.env.VITE_APP_NAME || 'Laravel'

// Tüm muhtemel konum/uzantıları tek haritada topla (key -> import function)
const pages = {
  ...import.meta.glob('./Pages/**/*.vue'),
  ...import.meta.glob('./pages/**/*.vue'),
  ...import.meta.glob('./Pages/**/*.{jsx,tsx}'),
  ...import.meta.glob('./pages/**/*.{jsx,tsx}'),
}

createInertiaApp({
  title: (title) => title,
  resolve: (name) => {
    // Aranacak olası anahtarlar
    const tryKeys = [
      `./Pages/${name}.vue`,
      `./pages/${name}.vue`,
      `./Pages/${name}.jsx`,
      `./Pages/${name}.tsx`,
      `./pages/${name}.jsx`,
      `./pages/${name}.tsx`,
    ]

    for (const key of tryKeys) {
      if (pages[key]) {
        // ÖNEMLİ: import fonksiyonunu değil, resolvePageComponent sonucunu döndür
        return resolvePageComponent(key, pages)
      }
    }

    // Teşhis için logla ve yine resolvePageComponent ile standart yolu dene
    console.error('Inertia page missing →', name)
    console.warn('Known page keys:', Object.keys(pages))
    return resolvePageComponent(`./Pages/${name}.vue`, pages)
  },
  setup({ el, App, props, plugin }) {
    const head = createHead()
    const app = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(head)
      .use(ZiggyVue)
      .directive('can', canDirective)

    // Global error handler for Vue errors
    app.config.errorHandler = (err, instance, info) => {
      console.error('[Vue Error]', err)
      console.error('[Vue Error Info]', info)

      // Log to error logger service
      errorLogger.logJsError(err, {
        component: instance?.$options?.name || instance?.$options?.__name || 'Unknown',
        lifecycle: info,
      })
    }

    // Global warning handler
    app.config.warnHandler = (msg, instance, trace) => {
      if (import.meta.env.MODE !== 'production') {
        console.warn('[Vue Warning]', msg)
        console.warn('[Vue Warning Trace]', trace)
      }
    }

    // Override route function to automatically inject tenant parameter
    const originalRoute = app.config.globalProperties.route

    // Create a wrapper function that works both in component context and globally
    const routeWrapper = function(name, params, absolute, config) {
      // If no name provided, return a safe object with current() and params
      if (!name) {
        try {
          const originalResult = originalRoute.call(this)
          return {
            current: (checkName) => {
              try {
                return originalRoute.call(this).current(checkName)
              } catch (e) {
                // Ziggy internal error - fallback to URL matching
                if (!checkName) return null
                const pathname = window.location.pathname
                if (checkName === 'admin.dashboard') return pathname === '/admin/dashboard' || pathname === '/admin'
                if (checkName.startsWith('admin.')) return pathname.startsWith('/admin')
                return false
              }
            },
            params: originalResult?.params || {}
          }
        } catch (e) {
          // If Ziggy completely fails, return safe defaults
          return {
            current: () => false,
            params: {}
          }
        }
      }

      // Central/landlord routes that should NEVER have tenant_slug added
      const centralRoutes = [
        'home', 'login', 'login.store', 'register', 'register.store',
        'password.request', 'password.email', 'password.reset', 'password.update',
        'pricing', 'contact', 'terms', 'privacy', 'kvkk',
        'auth.oauth.redirect', 'auth.oauth.callback',
        'qr-login', 'qr-login.store', 'employee-login', 'employee-login.store',
        'tenants.index', 'tenants.create', 'tenants.store',
        'tenants.show', 'tenants.switch', 'tenants.update', 'tenants.destroy',
        'tenants.demo-data.status', 'tenants.demo-data.load', 'tenants.demo-data.clear',
        'subscriptions.store', 'subscriptions.cancel', 'subscriptions.renew',
        'subscriptions.convert-trial', 'subscriptions.upgrade', 'subscriptions.downgrade'
      ]

      // Landlord admin routes (these are in routes/web.php, not routes/tenant.php)
      const landlordAdminRoutes = [
        'admin.dashboard',
        'admin.tenants',
        'admin.users',
        'admin.plans',
        'admin.modules',
        'admin.analytics',
        'admin.subscriptions',
        'admin.tickets',
        'admin.releases',
        'admin.error-reports'
      ]

      // Check if this is a central route - don't add tenant_slug
      const isCentralRoute = centralRoutes.includes(name) ||
                            landlordAdminRoutes.some(r => name === r || name.startsWith(r + '.'))

      if (isCentralRoute) {
        try {
          return originalRoute.call(this, name, params, absolute, config)
        } catch (e) {
          console.error('[Ziggy Error] Route generation failed:', name, e)
          return '#'
        }
      }

      // Try to get tenant from multiple sources (in priority order)
      let tenant = null

      // Source 1: Window global (set by Blade template)
      if (window.currentTenant) {
        tenant = window.currentTenant
      }
      // Source 2: Vue component context (this.$page)
      else if (this?.$page?.props?.tenant) {
        tenant = this?.$page?.props?.tenant
      }
      // Source 3: Inertia page props (for global/non-component usage)
      else if (router.page?.props?.tenant) {
        tenant = router.page?.props?.tenant
      }
      // Note: Removed URL path extraction fallback as it incorrectly identifies
      // central route names (like 'register') as tenant slugs

      // If we have a tenant context, ensure tenant_slug parameter is included
      // Subdomain routes use {tenant_slug} parameter in domain constraint
      if (tenant?.slug) {
        // @routes sayfaya `const Ziggy` yazar; const pencere özelliği olmadığı için window.Ziggy hep boştu: rota bilgisi
        // okunamıyor, parametre adı rota adından tahmin ediliyordu (çok parametreli rotalarda adres '#' dönüyordu)
        const ziggyRoutes = (typeof Ziggy !== 'undefined' ? Ziggy : window.Ziggy)?.routes
        params = normalizeRouteParams(name, params, tenant.slug, ziggyRoutes)
      }

      try {
        return originalRoute.call(this, name, params, absolute, config)
      } catch (e) {
        console.error('[Ziggy Error] Route generation failed:', name, params, e)
        return '#'
      }
    }

    // Set as global property for component access
    app.config.globalProperties.route = routeWrapper

    // Also expose globally for non-component usage (like in <script setup>)
    window.route = routeWrapper

    const mounted = app.mount(el)

    // Initialize theme system
    const { initTheme } = useTheme()
    initTheme()

    return mounted
  },
  progress: {
    color: '#4B5563',
  },
})
